import JSBridge from '@byted/secsdk-helpers/bd-turing-bridge/index2';
import axios from 'axios';
import * as logger from '../utils/logger';
import { debug } from '@/utils/debug';
import {
  JSB_TIMEOUT,
  isNative,
  SECURE_URL,
  useDialogSizeV2,
} from '@/utils/constants';
import debounce from 'lodash.debounce';
import {
  mergeUrl,
  isAndroid,
  isIOS,
  hasSettingsJSB,
  getStylePropertyValue,
} from '@/utils/utils';
// 因为可能在西瓜视频web端引入存在闪崩的情况， 所以在验证码单独引入bd-turing-bridge sdk进行了改造验证是否是我们的sdk影响了西瓜搜素的jsbridge sdk
const jsBridge = JSBridge.init(isNative);
const prefix = 'bytedcert';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface JSBResponse<T = any> {
  code: number;
  data: T;
}

function delay(ms: number, cmd: string) {
  return new Promise((resolve, reject) => {
    setTimeout(() => reject(new Error(`${cmd}: network timeout `)), ms);
  });
}

/**
 * @param cmd JSBRIDGE接口
 * @param args JSBRIDGE请求参数
 * @param setTimeout 设置是否开启超时限制，默认支持超时
 * @param ignoreError 设置是否忽略错误，默认不忽略
 * @param timeout 超时时间限制，单位ms
 */
function invoke<T = any>(
  cmd: string,
  args?: any,
  setTimeout: boolean = false,
  ignoreError: boolean = false,
  timeout?: number
): Promise<JSBResponse<T>> {
  debug(`${cmd} request JSBRIDGE args ******:`, args);
  const promise = new Promise<JSBResponse<T>>((resolve, reject) => {
    jsBridge.call(cmd, args, (result: any) => {
      debug(`${cmd} request JSBRIDGE result:`, result);
      if (ignoreError) {
        resolve(result);
      }
      // 非1表示jsb本身出错
      if (result.code === 1) {
        resolve(result.data);
      } else {
        reject(new Error(`jsb error, error code: ${result.code}`));
      }
    });
  });
  // 如果设置了timeout，说明需要超时设置，主要是解决新增接口在老版本不适配的问题
  if (!setTimeout && !timeout) return promise;
  return Promise.race([promise, delay(timeout || JSB_TIMEOUT, cmd) as any]);
}

/**
 * @param cmd 监听客户端JSBRIDGE消息
 * @param hasCallbackData 是否有回调数据
 */
function ListenInvoke(cmd: string, hasCallbackData: boolean = true) {
  return new Promise((resolve, reject) => {
    jsBridge.on(`${prefix}.${cmd}`, (result: any) => {
      debug(`listen${cmd} JSBRIDGE return result:`, result);
      if (hasCallbackData) {
        resolve(result.data);
      } else {
        resolve();
      }
    });
  });
}

/**
 * @param cmd 监听客户端JSBRIDGE消息
 * @param ignoreError 忽略错误
 */
function listen<T = any>(
  cmd: string,
  callback: (data: JSBResponse<T> | T) => void,
  ignoreError: boolean = false
) {
  cmd = `${prefix}.${cmd}`;
  const listener = (result: JSBResponse<T>) => {
    debug(`${cmd} listen JSBRIDGE return result:`, result);
    if (ignoreError) {
      callback(result);
    } else {
      if (result.code === 1) {
        // 非1表示jsb本身出错
        callback(result.data);
      } else {
        debug(new Error(`jsb error, error code: ${result.code}`));
      }
    }
  };
  jsBridge.on(cmd, listener);
  return () => {
    jsBridge.off(cmd, listener);
  };
}

// FE获取SDK采集的埋点数据 埋点文档 https://bytedance.feishu.cn/space/doc/doccnuSEEuSrtCFgkCVxnhJmKMg#
type TrackOptions = {
  challenge_id: string;
  challenge_mode: string;
  challenge_code: number;
};
export async function getData({
  challenge_code,
  challenge_id,
  challenge_mode,
}: TrackOptions) {
  try {
    // logger.trackEvent('verify_app_interactive', {
    //   mode: challenge_mode,
    //   challenge_id: challenge_id,
    //   challenge_code: challenge_code,
    //   result: 'success',
    //   interactive_type: 'bytedcert_getData',
    // });

    const res = await invoke(`${prefix}.getData`);
    return (res && res.data) || [];
  } catch (err) {
    // logger.trackEvent('verify_app_interactive', {
    //   mode: challenge_mode,
    //   challenge_id: challenge_id,
    //   challenge_code: challenge_code,
    //   result: 'fail',
    //   err: err.message,
    //   interactive_type: 'bytedcert_getData',
    // });
    throw err;
  }
}

const lastRect = {
  w: 0,
  h: 0,
};
// H5端主动通知SDK设置对话框的宽度和高度
export const dialogSize = debounce((rect?: any) => {
  let w = 300,
    h = 304;
  if (rect) {
    w = Math.floor(rect.w);
    h = Math.floor(rect.h);
  }
  // 如何弹窗尺寸没有发生变化，可以跳过，不继续调整弹窗尺寸
  if (w === lastRect.w && h === lastRect.h) {
    return;
  }
  lastRect.w = w;
  lastRect.h = h;
  if (useDialogSizeV2 === '1') {
    const dpr = window.devicePixelRatio || 2;
    invoke(`${prefix}.dialogSize_V2`, {
      w: w * dpr,
      h: h * dpr,
    });
  } else {
    // 对于有小数点的像素，需要手动向上取整
    invoke(`${prefix}.dialogSize`, {
      w,
      h,
    });
  }
}, 200);

export async function closePage(
  args: any,
  { challenge_code, challenge_id, challenge_mode }: TrackOptions
) {
  try {
    const result = invoke(`${prefix}.closePage`, args);
    // logger.trackEvent('verify_app_interactive', {
    //   mode: challenge_mode,
    //   challenge_id: challenge_id,
    //   challenge_code: challenge_code,
    //   result: 'success',
    //   interactive_type: 'bytedcert_closePage',
    // });
    return result;
  } catch (err) {
    // logger.trackEvent('verify_app_interactive', {
    //   mode: challenge_mode,
    //   challenge_id: challenge_id,
    //   challenge_code: challenge_code,
    //   result: 'fail',
    //   err: err.message,
    //   interactive_type: 'bytedcert_closePage',
    // });
  }
}

export async function getNativeTouch() {
  return invoke(`${prefix}.getTouch`, {}, true, true, 50);
}

export async function pageEnd(args: { log: any }) {
  return invoke(`${prefix}.pageEnd`, args);
}

export async function getSettings() {
  return invoke(`${prefix}.getSettings`, null, true);
}

// app 端， use_native_report == 1 时，在 app 端埋点
export async function TeaToNative(evt: Obj) {
  return invoke(`${prefix}.eventToNative`, evt);
}
// app 端， use_jsb_request == 1 时，get post 请求转到 native 端
export async function requestToNative(
  method: string | undefined,
  url: string | undefined,
  query: any,
  data?: any
) {
  const _data = await interceptors(url, query, data);
  const requestUrl = mergeUrl(axios.defaults.baseURL, _data.url);
  const param: {
    method: string | undefined;
    url: string | undefined;
    query: any;
    data?: any;
  } = {
    method,
    url: requestUrl,
    query: _data.query,
  };
  if (method !== 'get') {
    param.data = _data.data;
  }
  return new Promise((resolve, reject) => {
    // requestToNative 不走 invoke 的错误处理 ignoreError 设置为 true
    invoke(`${prefix}.network.request`, param, false, true).then((res) => {
      // 非1表示jsb本身出错
      if (res.code === 1) {
        if (res.data && res.data.code === 200) {
          resolve(res.data);
        } else {
          if (res.data) {
            reject(res.data);
          } else {
            reject(new Error('jsb warning, request fail return null'));
          }
        }
      } else {
        // jsb 本身的错误
        const err = new Error(`jsb error, error code: ${res.code}`);
        // @ts-ignore'
        err.code = 'JSBERROR';
        reject(err);
      }
    });
  });
}

// 在 get post 请求转到 native 前做一些 和 axios 拦截器做的事情
async function interceptors(url: string | undefined, query: any, data?: any) {
  return { url, query, data };
}

// 每次 chiallenge_id 更新后上报给 native 端
export async function updateChallengeId4Native(challenge_id: string) {
  return invoke(`${prefix}.h5_state_changed`, { challenge_id });
}

// 验证结果 枚举变量定义 参见https://bytedance.feishu.cn/space/doc/doccnG3TtcQt7vIi0OUYpReKVpd#
enum BDTuringVerifyStatus {
  BDTuringVerifyStatusOK = 0, ///< 验证通过
  BDTuringVerifyStatusError = 1, ///< 验证失败
  BDTuringVerifyStatusClose = 2, ///< 验证中断，如关闭了验证窗口
  BDTuringVerifyStatusNetworkError = 3, ///< 网络原因等，导致用户无法操作验证码
  BDTuringVerifyStatusCloseMask = 4, ///< 验证中断，用户点击蒙层
  BDTuringVerifyStatusCloseBack = 5, ///< 验证中断，用户点击返回按钮（只有安卓有）
  BDTuringVerifyStatusCloseApp = 6, ///< 验证中断，业务方主动关闭了验证页面
  BDTuringVerifyStatusCloseFeedback = 7, ///< 提交反馈成功后提交
}
// 验证回调结果
export function verifyResult(args: {
  result: BDTuringVerifyStatus;
  mode: string;
  type: 1 | 2; // 1代表短信上行 2代表图片验证码
}) {
  return invoke(`${prefix}.verifyResult`, args);
}

// 监听蒙层关闭：Native通知FE用户操作了关闭事件，在关闭之前可能需要做埋点，做完埋点之后再调用关闭弹窗事件
export function listenGoToClose() {
  return ListenInvoke('goToClose');
}

// 监听Native端事件：Native每次采集满200个事件主动给FE发送
export function listenVerifyReport() {
  return ListenInvoke('verifyReport');
}

export const listenORIChange = <T>(callback: (data: T) => void) => {
  return listen<T>('orientation_changing', callback as any);
};

/**
 * 关闭验证码前需要通过verifyResult接口通知native验证结果，同时还要通过getData接口从native端拿到还未发送的事件进行前端埋点（客户端会有一些触摸事件，交给fe进行埋点）
 * 如果有埋点失败的事件，需要在通过pageEnd接口发送给客户端暂存起来。
 */
export async function pageEndHandle(args: {
  verifyResultType: number;
  closeType: string;
  duration: number;
  challenge_id: string;
  challenge_mode: string;
  challenge_code: number;
}) {
  const { challenge_code, challenge_id, challenge_mode } = args;
  logger.trackEvent('close', {
    mode: challenge_mode,
    challenge_id,
    challenge_code,
    style: args.closeType,
    duration: args.duration,
  });
  // 调用verifyResult，会隐藏webview，但不会销毁。
  verifyResult({
    type: 2,
    result: args.verifyResultType,
    mode: challenge_mode,
  });
  const arr = await getData({
    challenge_code,
    challenge_id,
    challenge_mode,
  });
  const events = window.captchaFailEvents.concat(arr);
  await logger.batchSendEvents(events);
  const logsArr = window.captchaFailEvents || [];
  // 告诉native可以销毁当前webview
  pageEnd({
    log: {
      data: logsArr,
    },
  });
}
export async function pageEndHandle4Voice() {
  // 调用verifyResult，会隐藏webview，但不会销毁。
  verifyResult({
    type: 2,
    result: 0,
    mode: 'voice',
  });
  // 告诉native可以销毁当前webview
  pageEnd({
    log: {
      data: [],
    },
  });
}

export const fetchSettings = async (options: any): Promise<Settings> => {
  try {
    // IOS上JSB都有响应，不存在的JSB会异常的响应；Android上不存在的JSB永不响应
    if (isIOS(options) || (isAndroid(options) && hasSettingsJSB(options))) {
      const settings = await getSettings();
      return settings as Settings;
    } else {
      return {};
    }
  } catch (err) {
    return {};
  }
};
